/**
 * Kabuğun kurulum bilgileri: bağlı olunan firma ve (sistem rolünde) sunucu.
 *
 * Firma adı `api/system/AppInfo` ucundan geliyor — DAL onu Support'tan çözüp altı saat
 * önbelleğe alıyor. Tarayıcı Support'a doğrudan çıkmıyor: abonelik kimlikleri sunucu
 * yapılandırmasında duruyor.
 *
 * Sunucu bilgisi için AYRI bir uç var; eski `GetDbConnectionInfo` yanıtında bağlantı
 * parolası da bulunuyor ve onu tarayıcıya indirmek istemiyoruz.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';

export interface AppInfo {
  customerName: string;
  /**
   * Aboneliğin açıklaması. Aynı müşteride aynı üründen birden fazla abonelik
   * olabiliyor ("Üretim" / "Test" gibi) ve bunlar yalnızca bu alanla ayrılıyor.
   */
  description: string;
}

export interface ServerInfo {
  server: string;
  database: string;
}

/**
 * Firma adı.
 *
 * Herkese gösterilir — uçta da rol kısıtı yok. İki tuzak vardı:
 *
 * 1. Sonuç başarısızsa bir daha denenmiyordu (`retry: false`); Support'a ulaşılamayan
 *    tek bir an, o oturumda adın hiç görünmemesi demekti.
 * 2. BOŞ ad da başarılı sayılıp altı saat önbelleğe giriyordu; Support geçici olarak
 *    cevap vermediğinde ad yarım gün boyunca kayboluyordu. Ad boş dönerse önbellek
 *    kısa tutuluyor, dolu dönerse uzun.
 */
export function useAppInfo() {
  return useQuery({
    queryKey: ['shell', 'app-info'],
    queryFn: () => apiClient.get<AppInfo>(ENDPOINTS.SYSTEM_APP_INFO, { octo: { expectSuccess: false } }),
    staleTime: (query) => (query.state.data?.customerName ? 6 * 60 * 60_000 : 60_000),
    gcTime: 12 * 60 * 60_000,
    retry: 2,
  });
}

/** Yalnız sistem rolünde çağrılır; diğer rollerde uç zaten 403 döner. */
export function useServerInfo(enabled: boolean) {
  return useQuery({
    queryKey: ['shell', 'server-info'],
    queryFn: () =>
      apiClient.get<ServerInfo>(ENDPOINTS.SYSTEM_SERVER_INFO, { octo: { expectSuccess: false } }),
    enabled,
    staleTime: Infinity,
    gcTime: 60 * 60_000,
    retry: false,
  });
}

/** Abonelik kontenjanı — DAL Support'tan çözüp sayıları geçiriyor. */
export interface LicenseSummary {
  available: boolean;
  customerName: string;
  /** Aynı üründen birden fazla abonelik olduğunda onları ayıran açıklama. */
  description: string;
  maxUser: number;
  currentLicenseCount: number;
  remainingLicenses: number;
  maxNodeUser: number;
  currentNodeLicenseCount: number;
  remainingNodeLicenses: number;
  expirationDate: string;
}

/**
 * Kontenjan sayıları lisans verildikçe değişiyor, firma adı gibi sabit değil —
 * bu yüzden kısa önbellek. Ulaşılamazsa `available:false` döner ve çağıran
 * kontenjan bölümünü hiç çizmez.
 */
export function useLicenseSummary() {
  return useQuery({
    queryKey: ['shell', 'license-summary'],
    queryFn: () => apiClient.get<LicenseSummary>(ENDPOINTS.SYSTEM_LICENSE_SUMMARY, { octo: { expectSuccess: false } }),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
  });
}
